var _a;
const u = up.util;
up.Request = (_a = class Request extends up.Record {
        keys() {
            return [
                'method',
                'url',
                'hash',
                'params',
                'target',
                'failTarget',
                'headers',
                'timeout',
                'background',
                'cache',
                'expireCache',
                'evictCache',
                'layer',
                'mode',
                'context',
                'failLayer',
                'failMode',
                'failContext',
                'origin',
                'originLayer',
                'originMode',
                'builtAt',
                'wrapMethod',
                'contentType',
                'payload',
                'onLoading',
                'fail',
                'abortable',
                'lateDelay',
                'previews',
            ];
        }
        defaults() {
            let config = up.network.config;
            return {
                state: 'new',
                abortable: true,
                headers: {},
                timeout: config.timeout,
                builtAt: new Date(),
                previews: [],
                wrapMethod: config.wrapMethod,
            };
        }
        constructor(options) {
            super(options);
            this.params = new up.Params(this.params);
            this._normalize();
            if ((this.target || this.layer || this.origin) && !options.basic) {
                const layerLookupOptions = { origin: this.origin };
                this.layer = up.layer.get(this.layer, layerLookupOptions);
                this.context ||= this.layer.context || {};
                this.mode ||= this.layer.mode;
                this.failLayer = up.layer.get(this.failLayer, layerLookupOptions);
                this.failContext ||= this.failLayer?.context || {};
                this.failMode ||= this.failLayer?.mode;
                this.originLayer ||= up.layer.get(this.origin) || up.layer.current;
                this.originMode ||= this.originLayer?.mode;
            }
            this.bindLayer = options.bindLayer || this.layer;
            this._fragments = options.fragments;
            this._bindFragments = options.bindFragments;
            this._deferred = u.newDeferred();
            this._setAutoHeaders();
        }
        get effectiveLateTime() {
            if (this.background) {
                return false;
            }
            else {
                return this.lateDelay ?? u.evalOption(up.network.config.lateDelay, this);
            }
        }
        isTimed() {
            return u.isNumber(this.effectiveLateTime);
        }
        get xhr() {
            return this._xhr ??= new XMLHttpRequest();
        }
        get fragments() {
            return (this._fragments ||= this._findFragments());
        }
        _findFragments() {
            let steps = up.fragment.parseTargetSteps(this.target);
            let lookupOpts = { origin: this.origin, layer: this.layer };
            let matches = u.map(steps, (step) => up.fragment.get(step.selector, lookupOpts));
            return u.compact(matches);
        }
        get bindFragments() {
            return this._bindFragments || this.fragments;
        }
        get fragment() {
            return this.fragments?.[0];
        }
        _normalize() {
            this.method = u.normalizeMethod(this.method);
            this._extractHashFromURL();
            this._transferParamsToURL();
            this.url = u.normalizeURL(this.url);
        }
        _evictExpensiveAttrs() {
            u.task(() => {
                this.layer = undefined;
                this.failLayer = undefined;
                this.bindLayer = undefined;
                this.origin = undefined;
                this.originLayer = undefined;
                this._fragments = undefined;
                this._bindFragments = undefined;
            });
        }
        _extractHashFromURL() {
            let match = this.url?.match(/^([^#]*)(#.+)$/);
            if (match) {
                this.url = match[1];
                return this.hash = match[2];
            }
        }
        _transferParamsToURL() {
            if (!this.url || this.allowsPayload() || u.isBlank(this.params)) {
                return;
            }
            this.url = this.params.toURL(this.url);
            this.params.clear();
        }
        isSafe() {
            return up.network.isSafeMethod(this.method);
        }
        allowsPayload() {
            return u.methodAllowsPayload(this.method);
        }
        will302RedirectWithGET() {
            return this.isSafe() || (this.method === 'POST');
        }
        willCache() {
            return u.evalAutoOption(this.cache, up.network.config.autoCache, this);
        }
        runQueuedCallbacks() {
            u.always(this, () => this._evictExpensiveAttrs());
        }
        load() {
            if (this.state !== 'new')
                return;
            if (this._emitLoad()) {
                this.state = 'loading';
                this._normalize();
                this.onLoading?.();
                this.expired = false;
                new up.Request.XHRRenderer(this).buildAndSend({
                    onload: () => this._onXHRLoad(),
                    onerror: () => this._onXHRError(),
                    ontimeout: () => this._onXHRTimeout(),
                    onabort: () => this._onXHRAbort()
                });
                return true;
            }
            else {
                this.abort({ reason: 'Prevented by event listener' });
            }
        }
        runPreviews(renderOptions) {
            if (!this.ended && !this.fromCache) {
                this._revertPreviews = up.status.runPreviews(this, renderOptions);
            }
        }
        _emitLoad() {
            let event = this.emit('up:request:load', { log: ['Loading %s', this.description] });
            return !event.defaultPrevented;
        }
        loadPage() {
            up.network.abort();
            new up.Request.FormRenderer(this).buildAndSubmit();
        }
        _onXHRLoad() {
            const response = this._extractResponseFromXHR();
            const log = 'Loaded ' + response.description;
            this.emit('up:request:loaded', { request: response.request, response, log });
            this.respondWith(response);
        }
        _onXHRError() {
            this._setOfflineState('Network error');
        }
        _onXHRTimeout() {
            this._setOfflineState('Timeout');
        }
        _onXHRAbort() {
            this._setAbortedState();
        }
        abort({ reason } = {}) {
            if (this._setAbortedState(reason) && this._xhr) {
                this._xhr.abort();
            }
        }
        _setAbortedState(reason = 'Unknown reason') {
            if (this.ended)
                return;
            let log = ["Aborted request to %s: %s", this.description, reason];
            this.state = 'aborted';
            this._reject(new up.Aborted(log));
            this.emit('up:request:aborted', { log });
            return true;
        }
        _setOfflineState(reason = 'Unknown reason') {
            if (this.ended)
                return;
            let log = ['Cannot load request from %s: %s', this.description, reason];
            this.state = 'offline';
            this.emit('up:request:offline', { log });
            this._reject(new up.Offline(log, { reason, request: this }));
        }
        respondWith(response) {
            this.response = response;
            if (this.ended)
                return;
            this.state = 'loaded';
            if (response.ok) {
                this._resolve(response);
            }
            else {
                this._reject(response);
            }
        }
        _resolve(response) {
            this._onSettle();
            this._deferred.resolve(response);
        }
        _reject(responseOrError) {
            this._onSettle();
            this._deferred.reject(responseOrError);
        }
        _onSettle() {
            this._revertPreviews?.();
        }
        get ended() {
            return (this.state !== 'new') && (this.state !== 'loading') && (this.state !== 'tracking');
        }
        csrfHeader() {
            return up.protocol.csrfHeader();
        }
        csrfParam() {
            return up.protocol.csrfParam();
        }
        csrfToken() {
            if (!this.isSafe() && !this.isCrossOrigin()) {
                return up.protocol.csrfToken();
            }
        }
        isCrossOrigin() {
            return u.isCrossOrigin(this.url);
        }
        _extractResponseFromXHR() {
            const responseAttrs = {
                method: this.method,
                url: this.url,
                request: this,
                xhr: this.xhr,
                text: this.xhr.responseText,
                status: this.xhr.status,
                title: up.protocol.titleFromXHR(this.xhr),
                target: up.protocol.targetFromXHR(this.xhr),
                openLayer: up.protocol.openLayerFromXHR(this.xhr),
                acceptLayer: up.protocol.acceptLayerFromXHR(this.xhr),
                dismissLayer: up.protocol.dismissLayerFromXHR(this.xhr),
                eventPlans: up.protocol.eventPlansFromXHR(this.xhr),
                context: up.protocol.contextFromXHR(this.xhr),
                expireCache: up.protocol.expireCacheFromXHR(this.xhr),
                evictCache: up.protocol.evictCacheFromXHR(this.xhr),
                fail: this.fail,
            };
            let methodFromResponse = up.protocol.methodFromXHR(this.xhr);
            let urlFromResponse = up.protocol.locationFromXHR(this.xhr);
            if (urlFromResponse) {
                if (!u.matchURLs(this.url, urlFromResponse)) {
                    methodFromResponse ||= 'GET';
                }
                responseAttrs.url = urlFromResponse;
            }
            if (methodFromResponse) {
                responseAttrs.method = methodFromResponse;
            }
            return new up.Response(responseAttrs);
        }
        _buildEventEmitter(args) {
            return up.EventEmitter.fromEmitArgs(args, {
                layer: this.bindLayer,
                request: this,
                origin: this.origin
            });
        }
        emit(...args) {
            return this._buildEventEmitter(args).emit();
        }
        assertEmitted(...args) {
            this._buildEventEmitter(args).assertEmitted();
        }
        get description() {
            return this.method + ' ' + this.url;
        }
        isBoundToSubtrees(subtreeRoots) {
            subtreeRoots = u.wrapList(subtreeRoots);
            return u.some(this.bindFragments, function (fragment) {
                return u.some(subtreeRoots, (subtreeElement) => subtreeElement.contains(fragment));
            });
        }
        isBoundToLayers(layers) {
            return u.contains(layers, this.bindLayer);
        }
        get age() {
            return new Date() - this.builtAt;
        }
        header(name) {
            return this.headers[name];
        }
        _setAutoHeaders() {
            for (let key of ['target', 'failTarget', 'mode', 'failMode', 'context', 'failContext', 'originMode']) {
                this._setPropertyHeader(key);
            }
            let csrfHeader, csrfToken;
            if ((csrfHeader = this.csrfHeader()) && (csrfToken = this.csrfToken())) {
                this._setAutoHeader(csrfHeader, csrfToken);
            }
            this._setAutoHeader(up.protocol.headerize('version'), up.version);
        }
        _setPropertyHeader(key) {
            this._setAutoHeader(up.protocol.headerize(key), this[key]);
        }
        _setAutoHeader(name, value) {
            if (u.isMissing(value)) {
                return;
            }
            if (u.isOptions(value) || u.isArray(value)) {
                value = u.safeStringifyJSON(value);
            }
            this.headers[name] = value;
        }
        mergeIfUnsent(trackingRequest) {
            if (this.state !== 'new')
                return;
            if (!this.target || !trackingRequest.target)
                return;
            let targetAtoms = up.fragment.splitTarget(this.target + ',' + trackingRequest.target);
            this.target = u.uniq(targetAtoms).join(', ');
            this._setPropertyHeader('target');
            this._fragments = u.uniq([...this.fragments, ...trackingRequest.fragments]);
        }
        static tester(condition, { except } = {}) {
            let testFn;
            if (u.isFunction(condition)) {
                testFn = condition;
            }
            else if (condition instanceof this) {
                testFn = (request) => condition === request;
            }
            else if (u.isString(condition)) {
                let pattern = new up.URLPattern(condition);
                testFn = (request) => pattern.test(request.url);
            }
            else {
                testFn = (_request) => condition;
            }
            if (except) {
                return (request) => !up.cache.willHaveSameResponse(request, except) && testFn(request);
            }
            else {
                return testFn;
            }
        }
    },
    (() => {
        u.delegatePromise(_a.prototype, function () { return this._deferred; });
    })(),
    _a);
